import React, {useMemo} from 'react';
import ReactApexChart from 'react-apexcharts';
import {fetchOverviewAlertsChart} from '../../../requests/queries/flows';
import {getPeriod} from '../../FraudDetection/Reconciliation/constants';
import {generateSeverityColors} from '../helpers';
import {Loader} from '../../../components';
import {useGatedQuery} from '../../FraudDetection/Flows/hooks/useGatedQuery';

const EventsTrendChartGraph = ({series, categories, severity, loading}) => {
  const options = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {show: false},
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '20%',
        borderRadius: 3,
      },
    },
    dataLabels: {enabled: false},
    xaxis: {
      categories: categories || [],
      labels: {
        rotate: -45,
        style: {colors: '#5C688E', fontSize: '14px'},
      },
    },
    colors: generateSeverityColors(severity) || [
      '#48FAF2',
      '#DC6803',
      '#FF5C5C',
    ],
    legend: {
      show: true,
      fontSize: '12px',
      position: 'top',
      horizontalAlign: 'left',
      offsetX: -40,
      markers: {
        width: 10,
        height: 10,
        strokeWidth: 0,
        strokeColor: '#fff',
        radius: 12,
        shape: 'circle',
        offsetX: 0,
        offsetY: 0,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    grid: {
      xaxis: {lines: {show: false}},
      yaxis: {lines: {show: true}},
      borderColor: 'rgba(0, 0, 0, 0.03)',
    },
    fill: {opacity: 1},
  };

  return (
    <div id="chart">
      {loading ? (
        <Loader height={25} />
      ) : (
        <ReactApexChart
          className="project_line_chart bg-white"
          options={options}
          series={series}
          type="bar"
          height={350}
        />
      )}
    </div>
  );
};

export const EventsTrendChart = ({customElements, periodType, filters}) => {
  const {data, isLoading: overviewLoading} = useGatedQuery(
    ['overview-d-c', {params: {period: periodType, ...filters}}],
    () => fetchOverviewAlertsChart({params: {period: periodType, ...filters}}),
  );

  const chartData = useMemo(
    () => (!overviewLoading && data ? data : []),
    [data, overviewLoading],
  );

  const uniqueDates = useMemo(() => {
    if (!chartData) return [];
    const dates = [...new Set(chartData.map(item => item.createdAt))];
    return dates.sort((a, b) => new Date(a) - new Date(b));
  }, [chartData]);

  const uniqueSeverities = useMemo(() => {
    if (!chartData) return [];
    return [...new Set(chartData.map(item => item.severity))];
  }, [chartData]);

  const series = useMemo(
    () =>
      uniqueSeverities.map(severityName => ({
        name: severityName,
        data: uniqueDates.map(date => {
          const found = chartData.find(
            d => d.createdAt === date && d.severity === severityName,
          );
          return found ? parseInt(found.count, 10) : 0;
        }),
      })),
    [uniqueSeverities, uniqueDates, chartData],
  );

  const categories = useMemo(
    () => uniqueDates.map(date => getPeriod(date, periodType)),
    [uniqueDates, periodType],
  );

  return (
    <div className="bg-white relative rounded-xl w-full h-[350px]">
      <div className="flex absolute -top-[58px] z-10 flex-col -right-5 lg:flex-row gap-5 lg:gap-0 justify-between items-center px-6">
        {customElements && chartData?.length > 0 && customElements}
      </div>
      <EventsTrendChartGraph
        categories={categories}
        series={series}
        severity={uniqueSeverities}
        loading={overviewLoading}
      />
    </div>
  );
};

export default EventsTrendChart;
